import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useCollaborationInviteActions() {
  const queryClient = useQueryClient();

  const acceptInvite = useMutation({
    mutationFn: async ({ entityType, entityId }: { entityType: string; entityId: string }) => {
      const { data, error } = await supabase.rpc("accept_collaboration_invite", {
        p_entity_type: entityType,
        p_entity_id: entityId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      toast.success("Invitation accepted");
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["user-lists"] });
      queryClient.invalidateQueries({ queryKey: ["harmony-lists"] });
      queryClient.invalidateQueries({ queryKey: ["list-collaborators"] });
      queryClient.invalidateQueries({ queryKey: ["task-collaborators"] });
      queryClient.invalidateQueries({ queryKey: ["shared-lists"] });
      queryClient.invalidateQueries({ queryKey: ["pending-invites"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to accept invitation");
    },
  });

  const declineInvite = useMutation({
    mutationFn: async ({ entityType, entityId }: { entityType: string; entityId: string }) => {
      const { data, error } = await supabase.rpc("decline_collaboration_invite", {
        p_entity_type: entityType,
        p_entity_id: entityId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Invitation declined");
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["pending-invites"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to decline invitation");
    },
  });

  return {
    acceptInvite,
    declineInvite,
    isUpdating: acceptInvite.isPending || declineInvite.isPending,
  };
}
